'use strict';

import {changeLangToCurrent, getCurrentLang} from '../langSwitcher/langSwitcher';
import {isMobile, isNoMenu, settingsData, TRoot} from "../../../settings";
import {BaseElement} from "../../interfaces/BaseElement";
import {BmbyMode} from "../../mode/BmbyMode";
import {ViewMode} from "../../../common";

export class Disclaimer {
	#element = document.createElement('span');
	#text = 'Lorem ipsum dolor sit amet, consectetur adipiscing elit.';
	#additionalClassName;

	constructor(parent, text, className) {
		if (!parent)
			return;

		if (className)
			this.#additionalClassName = className;
		if (this.#additionalClassName)
			this.#element.classList.add(this.#additionalClassName);

		if (isNoMenu)
			this.#element.classList.add('no-menu-mode');

		if (text)
			this.#text = text;

		if (isMobile && className === 'disclaimer__main-screen' && settingsData.activeViewMode === ViewMode.Cylinder)
			this.#element.classList.add('hidden');


		this.#element.classList.add('disclaimer', 'language-string');
		this.#setText();

		parent.appendChild(this.#element);
	}

	set text(value) {
		if (!this.#element) return;

		this.#text = value;
		this.#setText();
	}

	set style(value) {
		if (!this.#element) return;

		this.#element.style[value.style] = value.value;
	}

	set textHtml(value) {
		if (!this.#element) return;
		this.#text = value;
		this.#element.innerHTML = this.#text;
	}

	get element() {
		return this.#element;
	}

	#setText() {
		const IS_LONG = isMobile ? 55 : 110;

		const addDisclaimerBackupText = () => {
			this.#element.classList.remove('language-string');
			this.#element.style.cursor = 'pointer';
			new BaseElement('span')
				.addClass('disclaimer__text', 'language-string')
				.addAttribute('data-dictionary', 'disclaimerBackup')
				.innerHtml(changeLangToCurrent('disclaimerBackup'))
				.appendTo(this.#element);
			new BaseElement('span')
				.addClass('disclaimer__info-icon', 'icon-info')
				.appendTo(this.#element);
		}

		if (Array.isArray(this.#text)) {
			const englishTranslationLength = this.#text.find(item => item?.lang === 'en')?.value.length;
			if (englishTranslationLength > IS_LONG) {
				this.#element.addEventListener('click', () => {
					this.createDisclaimerPopUp(changeLangToCurrent(this.#text.find(item => item?.lang === getCurrentLang())?.value));
				});
				addDisclaimerBackupText();
			} else {
				this.#text.forEach(item => {
					if (item && item.value.length) {
						this.#element.setAttribute(`data-lang-${item.lang}`, item?.value);
					} else {
						const value = config.dictionary['disclaimerBackup'][item.lang] || config.dictionary['disclaimerBackup'].en;
						item.value = value;
						this.#element.setAttribute(`data-lang-${item.lang}`,value);
					}
				});
				this.#element.innerHTML = changeLangToCurrent(this.#text.find(item => item?.lang === getCurrentLang())?.value);
			}
		} else {
			if (config.dictionary?.[this.#text]?.['en']?.length > IS_LONG) {
				this.#element.addEventListener('click', () => {
					this.createDisclaimerPopUp(changeLangToCurrent(this.#text));
				});
				addDisclaimerBackupText();
			} else {
				this.#element.setAttribute('data-dictionary', this.#text);
				this.#element.innerHTML = changeLangToCurrent(this.#text);
			}
		}
	}

	setStyle(styleName, value) {
		if (!this.#element) return;

		this.#element.style[`${styleName}`] = value;
	}

	addClass(className) {
		if (!this.#element) return;

		this.#element.classList.add(className);
	}

	show() {
		if (!this.#element) return;

		this.#element.classList.remove('hidden');
	}

	hide() {
		if (!this.#element) return;

		this.#element.classList.add('hidden');
	}

	createDisclaimerPopUp(text) {
		if (TRoot.querySelector('.disclaimer__pop-up'))
			return;

		const additionalClass = this.#additionalClassName === 'options-card__disclaimer' ? 'options-card__disclaimer-pop-up' : undefined;
		const popUp = new BaseElement()
			.addClass('disclaimer__pop-up', additionalClass)
			.appendTo(TRoot);
		new BaseElement('span')
			.addClass('disclaimer__pop-up-text')
			.innerHtml(text)
			.appendTo(popUp.DOMElement);
		new BaseElement('button')
			.addClass('disclaimer__pop-up-close', 'close-button-cross')
			.event('click', () => {
				popUp.remove();
			})
			.appendTo(popUp.DOMElement);

		const disclaimerClickPopUpHandler = (event) => {
			if (event.target.closest('.disclaimer__pop-up') || event.target.closest('.disclaimer'))
				return;

			popUp.remove();
			window.removeEventListener('click', disclaimerClickPopUpHandler);
		};
		window.addEventListener('click', disclaimerClickPopUpHandler);
	}
}

export class LegalComments extends Disclaimer {
	constructor(mesh, parent, text) {
		super(parent, text);

		this.comments = mesh ? mesh.userData.crm_data.assets.legalComments : null;
	}

	setComment(type) {
		let text = '';

		switch (type) {
			case '2D':
				text = Array.isArray(this.comments?.plan2D) ?
					this.comments.plan2D.find(obj => obj.lang === getCurrentLang()).value : null;
				if (text) {
					this.textHtml = text;
					this.setTranslation(this.comments.plan2D);
				} else {
					this.text = '2dDisclaimer';
				}
				break;
			case '360Sample':
				text = Array.isArray(this.comments?.tours360) ?
					this.comments.tours360.find(obj => obj.lang === getCurrentLang()).value : null;
				if (text) {
					this.textHtml = text;
					this.setTranslation(this.comments.tours360);
				} else {
					this.text = '360sampleDisclaimer';
				}
				break;
			case '360':
				text = Array.isArray(this.comments?.tours360) ?
					this.comments.tours360.find(obj => obj.lang === getCurrentLang()).value : null;
				if (text) {
					this.textHtml = text;
					this.setTranslation(this.comments.tours360);
				} else {
					this.text = '360Disclaimer';
				}
				break;
			case 'vantage':
				text = Array.isArray(this.comments?.vantage) ?
					this.comments.vantage.find(obj => obj.lang === getCurrentLang()).value : null;
				if (text) {
					this.textHtml = text;
					this.setTranslation(this.comments.vantage);
				} else {
					this.text = 'vantageDisclaimer';
				}
				break;
			case 'gallery':
				text = Array.isArray(this.comments?.gallery) ?
					this.comments.gallery.find(obj => obj.lang === getCurrentLang()).value : null;
				if (text) {
					this.textHtml = text;
					this.setTranslation(this.comments.gallery);
				} else {
					this.text = 'optionsCardGalleryDisclaimer';
				}
				break;
			default:
				this.hide();
		}
	}

	setTranslation(commentsArray) {
		this.element.removeAttribute('data-dictionary');
		commentsArray.forEach(comment => {
			this.element.setAttribute(`data-lang-${comment.lang}`, comment.value);
		});
	}
}
